import React from "react";
import { Link } from "react-router-dom";
import { Form, Input, Select, Button } from "antd";
import { GET_USERS_CURSOR } from "graphql/query/user-gql";

import { teamUseMutation } from "../../hooks";

import User from "components/user";
import { Localize, EntityRemoveButton } from "components/service";
import { localizeByKey } from "components/service/localize";
import { FormElements } from "components/layout";
import { TTeam } from "components/team/helpers/team-types-helper";
import Icons from "components/icons";
import { PermissionButton, useModelFormValidation, usePermissionByObject } from "components/permissions";
import { useQuery } from "@apollo/client";
import { GET_ME } from "graphql/query/user-gql";
import Team from "components/team";

interface ITeamEditFormProps
{
  team: TTeam | undefined;
}

interface ITeamData
{
  [ role: string ]: number[];
}

interface IInitialValueItem
{
  value: string;
  label: string;
}

const TeamEditForm: React.FC<ITeamEditFormProps> = ({ team }) =>
{
  const [ form ] = Form.useForm();
  const [ teamType, setTeamType ] = React.useState<string>("full");

  const { _setTeamCreateUpdate, loadingMutationTeamCreateUpdate } =
    teamUseMutation.createUpdate(team?.id);

  const { _setMutationTeamDelete, loadingMutationTeamDelete } =
    teamUseMutation.delete(team?.id);

  const { validateUpdatePermission, hasUpdatePermission } = useModelFormValidation(Team.Const);

  // Memoize the permission object to prevent unnecessary re-renders
  const editTeamHeadOnlyPermission = React.useMemo(() => ({
    slug: "permission_edit_team_head_only",
    groupName: "permission_group_teams",
    entryPoint: "library",
    action: "permission_action_edit"
  }), []);

  const { hasPermission: hasEditTeamHeadOnlyPermission } = usePermissionByObject(editTeamHeadOnlyPermission);
  const { data: meData } = useQuery(GET_ME);

  // Memoize the check for team head
  const isCurrentUserTeamHead = React.useMemo(() => {
    return meData?.me?.id && team?.relHead?.id && meData.me.id === team.relHead.id;
  }, [meData?.me?.id, team?.relHead?.id]);

  // Memoize the permission logic (same for update and delete)
  const canPerformActions = React.useMemo(() => {
    return hasUpdatePermission && (!isCurrentUserTeamHead || hasEditTeamHeadOnlyPermission);
  }, [hasUpdatePermission, isCurrentUserTeamHead, hasEditTeamHeadOnlyPermission]);

  function generateTeamData(data: ITeamData)
  {
    const result: { team_role: string; user_id: number }[] = [];

    for (const role in data) {
      const users = data[ role ];

      if (Array.isArray(users)) {
        users.forEach((userId) =>
        {
          result.push({
            team_role: role,
            user_id: userId,
          });
        });
      }
    }

    return result;
  }

  function getInitialValueForRole(role: string): IInitialValueItem[] | undefined
  {
    return team?.relTeamUsers.length
      ? team?.relTeamUsers
        .filter((item) => item.team_role === role)
        .map(({ relUser: { id, name, surname } }) => ({
          value: id,
          label: `${name} ${surname}`,
        }))
      : undefined;
  }

  const valueFieldUser = Form.useWatch("head_id", form);
  const typeField = Form.useWatch("type", form);

  // Reusable component for buttons with permission check
  const ConditionalPermissionButton = React.useCallback(({
    permission,
    canPerform,
    children,
    ...props
  }: {
    permission: any;
    canPerform: boolean;
    children: React.ReactNode;
    [key: string]: any;
  }) => {
    if (canPerform) {
      return (
        <PermissionButton permission={permission} disabledFallback={true} {...props}>
          {children}
        </PermissionButton>
      );
    } else {
      return (
        <Button disabled={true} {...props}>
          {children}
        </Button>
      );
    }
  }, []);

  React.useEffect(() =>
  {
    if (typeField) {
      setTeamType(typeField);
    }
  }, [ typeField ]);

  return (
    <Form
      key="edit-team-form"
      layout="vertical"
      form={form}
      className="model-form edit-team-form"
      onFinish={(values) =>
      {
        if (!validateUpdatePermission()) {
          return;
        }

        _setTeamCreateUpdate({
          variables: {
            input: {
              id: team?.id ? +team.id : undefined,
              title: values.title,
              head_id: values.head_id,
              team_user: generateTeamData(values),
              type: values.type,
            },
          },
        });
      }}
    >
      <div className="row-grid grid-gap-10 row-gap-0 col-md-2">
        <Form.Item
          name="title"
          label={<Localize>FORMS.Input_Label_TeamName</Localize>}
          initialValue={team?.title}
          rules={[
            {
              required: true,
              message: <Localize>FORM_RULES.Required_TeamName</Localize>,
            },
          ]}
        >
          <Input name="title" />
        </Form.Item>

        <FormElements.RadioSelect
          form={form}
          query={GET_USERS_CURSOR}
          name="head_id"
          model="usersCursor"
          modelCursorLabel={[ "name", "surname" ]}
          label="FORMS.Input_Label_Head"
          modalTitle="FORMS.Title_AddHead"
          tagInfo={
            valueFieldUser && (
              <Link
                className="select-link"
                to={`${User.Const.basePath}/${valueFieldUser}`}
              >
                <Icons.Information />
              </Link>
            )
          }
          initialValue={
            team?.relHead?.id
              ? [
                {
                  value: team.head_id,
                  label: `${team.relHead.name} ${team.relHead.surname}`,
                },
              ]
              : undefined
          }
        />

        <Form.Item
          name="type"
          label={<Localize>FORMS.Input_Label_Type</Localize>}
          initialValue={team?.type || "full"}
          rules={[
            {
              required: true,
              message: <Localize>FORM_RULES.Required_TeamType</Localize>,
            },
          ]}
        >
          <Select
            suffixIcon={<Icons.Arrow />}
            placeholder={localizeByKey("FORMS.Input_Label_Type").toString()}
          >
            <Select.Option key="full" value="full">
              <Localize>FORMS.Select_OptionLabel_Full</Localize>
            </Select.Option>
            <Select.Option key="cad" value="cad">
              <Localize>FORMS.Select_OptionLabel_CAD</Localize>
            </Select.Option>
          </Select>
        </Form.Item>
      </div>



      {team?.id && (
        <>
          {teamType === "full" && (
            <FormElements.CheckboxSelect
              form={form}
              query={GET_USERS_CURSOR}
              model="usersCursor"
              name="project_manager"
              label="FORMS.Input_Label_ProjectManagers"
              modalTitle="FORMS.Title_AddProjectManagers"
              modalButtonText="MODAL.Button_Text_AddMore"
              modelCursorLabel={[ "name", "surname" ]}
              initialValue={getInitialValueForRole("project_manager")}
              skipAddButton={!canPerformActions}
              disabledTags={!canPerformActions ? (getInitialValueForRole("project_manager")?.map(item => item.value) || []) : []}
            />
          )}

          {teamType === "full" && (
            <FormElements.CheckboxSelect
              form={form}
              query={GET_USERS_CURSOR}
              model="usersCursor"
              name="construction_manager"
              label="FORMS.Input_Label_ConstructionManagers"
              modalTitle="FORMS.Title_AddConstructionManagers"
              modalButtonText="MODAL.Button_Text_AddMore"
              modelCursorLabel={[ "name", "surname" ]}
              initialValue={getInitialValueForRole("construction_manager")}
              skipAddButton={!canPerformActions}
              disabledTags={!canPerformActions ? (getInitialValueForRole("construction_manager")?.map(item => item.value) || []) : []}
            />
          )}

          <FormElements.CheckboxSelect
            form={form}
            query={GET_USERS_CURSOR}
            model="usersCursor"
            name="draftsmen"
            label="FORMS.Input_Label_AutocadEngineers"
            modalTitle="FORMS.Title_AddAutocadEngineers"
            modalButtonText="MODAL.Button_Text_AddMore"
            modelCursorLabel={[ "name", "surname" ]}
            initialValue={getInitialValueForRole("draftsmen")}
            skipAddButton={!canPerformActions}
            disabledTags={!canPerformActions ? (getInitialValueForRole("draftsmen")?.map(item => item.value) || []) : []}
          />
        </>
      )}

      <div className="form-btn-holder">
{team?.id && (
          <EntityRemoveButton
            modalButton={
              <ConditionalPermissionButton
                permission={Team.Const.permissions.DELETE}
                canPerform={canPerformActions}
                icon={<Icons.Delete />}
              >
                <span className="ellipsis">
                  <Localize>TEAM.Button_Text_Delete</Localize>
                </span>
              </ConditionalPermissionButton>
            }
            nameEntity={
              localizeByKey("ENTITY.Modal_Title_Team")
            }
            dataNameEntity={`${team?.title}`}
            loading={loadingMutationTeamDelete}
            deleteMutation={(options) =>
            {
              if (!canPerformActions) {
                return Promise.reject(new Error('Access denied'));
              }
              return _setMutationTeamDelete(options);
            }}
            variables={{
              id: team?.id,
            }}
          />
        )}
        <ConditionalPermissionButton
          permission={Team.Const.permissions.UPDATE}
          canPerform={canPerformActions}
          htmlType="submit"
          className="btn-right"
          type="primary"
          icon={team?.id ? <Icons.Edit /> : <Icons.Plus />}
          loading={loadingMutationTeamCreateUpdate}
        >
          <span className="ellipsis">
            {team?.id ? (
              <Localize>GLOBAL.Button_Text_SaveChanges</Localize>
            ) : (
              <Localize>GLOBAL.Button_Text_CreateNew</Localize>
            )}
          </span>
        </ConditionalPermissionButton>
      </div>
    </Form>
  );
};

export default TeamEditForm;